/**
 * Taking the respondent to the problem. A failed submit or Next sets the
 * per-control errors; these helpers then move the page and the keyboard to
 * the first of them, in DOM order — which is reading order, which is the
 * order the summary lists them in.
 *
 * Every errored control carries `aria-invalid="true"` and
 * `data-error-path="<path>"` (see FieldInput). A control that cannot itself
 * take focus — a radio group, a signature pad — hands focus to the first
 * focusable thing inside it.
 */

const FOCUSABLE = 'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), canvas[tabindex], [tabindex]:not([tabindex="-1"])';

function focusTarget(el: Element): HTMLElement | null {
  const h = el as HTMLElement;
  if (typeof h.focus === 'function' && (h.matches('input, select, textarea, button') || h.hasAttribute('tabindex'))) return h;
  return (h.querySelector(FOCUSABLE) as HTMLElement | null) || (typeof h.focus === 'function' ? h : null);
}

/** Scroll a control into the middle of the viewport and give it the keyboard. */
export function bringTo(el: Element | null): boolean {
  if (!el) return false;
  const target = focusTarget(el);
  if (!target) return false;
  try { (el as HTMLElement).scrollIntoView?.({ block: 'center', behavior: 'smooth' }); } catch { /* jsdom */ }
  try { target.focus({ preventScroll: true }); } catch { try { target.focus(); } catch { /* not focusable */ } }
  return document.activeElement === target;
}

/** Focus the first errored control inside `root` (the whole document by default). */
export function focusFirstError(root: ParentNode = document): boolean {
  return bringTo(root.querySelector('[aria-invalid="true"]'));
}

/** Focus the control an error path names — what a summary item does when clicked. */
export function focusErrorPath(path: string, root: ParentNode = document): boolean {
  const esc = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(path) : path.replace(/["\\]/g, '\\$&');
  return bringTo(root.querySelector(`[data-error-path="${esc}"]`));
}

/**
 * Run after React has painted the errors: the attributes the query looks for
 * are set by the render that follows a state change, so the walk waits one
 * frame. `setTimeout` rather than `requestAnimationFrame` so it also fires
 * under jsdom, where the tests assert on `document.activeElement`.
 */
export function afterPaint(fn: () => void): void {
  setTimeout(fn, 0);
}
